/**
 * Client galleries (Pic-Time embed). The page is its own page load, in and out (Trera, 25 Oct 2026).
 *
 * Pic-Time's script (artgalleryembed.js) adds a window scroll listener that posts into the gallery frame and gives no
 * way to remove it. In the single-page site the frame went away when a client left /gallery but the listener stayed,
 * so every scroll on every later page threw "Cannot read properties of null (reading 'postMessage')". Third-party
 * embeds like this belong in their own document: leaving it throws away everything the script set up.
 *
 *   in   the links to /gallery are full page loads (navbar, homepage); any other way in reloads once
 *   out  internal links on this page are full page loads; anything else that leaves (a button, the back button
 *        inside the site) reloads the next page
 */
import { useEffect } from 'react'

const PICTIME_SCRIPT_SRC = 'https://embedding.pic-time.com/pictures/scripts/compiled/artgalleryembed.js'
// The path this document was loaded on (this module is imported when the site starts, not lazily)
const DOCUMENT_PATH = typeof window !== 'undefined' ? window.location.pathname : ''
const isGalleryPath = path => /^\/gallery\/?$/.test(path)

const Gallery = () => {
  const ownDocument = isGalleryPath(DOCUMENT_PATH)

  useEffect(() => {
    // Came in without a page load: load /gallery as its own page first
    if (!ownDocument) {
      window.location.replace(window.location.href)
      return undefined
    }

    if (!document.querySelector('script[src*="pic-time.com"]')) {
      const script = document.createElement('script')
      script.src = PICTIME_SCRIPT_SRC
      script.async = true
      document.body.appendChild(script)
    }

    // Internal links leave with a full page load (capture phase, before the router sees the click)
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const a = e.target instanceof Element ? e.target.closest('a[href]') : null
      if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return
      const url = new URL(a.href, window.location.href)
      if (url.origin !== window.location.origin || isGalleryPath(url.pathname)) return
      e.preventDefault()
      e.stopPropagation()
      window.location.assign(url.href)
    }
    document.addEventListener('click', onClick, true)

    return () => {
      document.removeEventListener('click', onClick, true)
      // Left some other way (a button, browser back within the site): load the next page fresh
      if (!isGalleryPath(window.location.pathname)) window.location.reload()
    }
  }, [ownDocument])

  if (!ownDocument) return <div className="bg-background" style={{ height: '200vh' }} />

  return (
    <div className="bg-background">
      {/* Pic-Time Gallery Embed - tall enough to scroll, footer appears at bottom after scrolling */}
      <iframe
        id="pictimeIntegration"
        src="https://client.loveandphotos.com/client?headless=true"
        frameBorder="0"
        title="Love & Photos Gallery"
        className="w-full"
        style={{ height: '200vh' }}
        allow="fullscreen"
      />
    </div>
  )
}

export default Gallery
